import {
  Box,
  Flex,
  Text,
  Divider,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { DataLake, Util } from '@finley-technologies/finley-types';

import { BindingContainer } from './BindingContainer';
import { LenderAthenaQueryPreviewDrawer } from './LenderAthenaQueryPreviewDrawer';
import { AthenaQueryStringWithSchemaSidebarContainer } from 'packages/athena/ui/AthenaQueryStringWithSchemaSidebarContainer';
import { colors } from 'packages/common-frontend/colors';
import {
  DeleteIconButton,
  CopyIconButton,
} from 'packages/common-frontend/components/IconButton';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteConfirmationModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { QueryInputTypeBadge } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/covenantPostgresQuery/lenderView/QueryInputTypeBadge';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import { AddQueryInputModal } from 'packages/settings/ui/AddQueryInputModal';

export const createAthenaQueryShell = (
  idx: number,
  name?: string,
): CreditAgreement.Config.AthenaConfig.Query => ({
  query: {
    name: name ?? `queryName${idx}`,
    queryString: 'SQL QUERY GOES HERE;',
  },
  variables: [],
  bindings: [],
  columnNames: ['placeholderColumn'],
});

type LenderAthenaQueryViewProps = {
  athenaQuery: CreditAgreement.Config.AthenaConfig.Query;
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  duplicateParams: {
    isDuplicateQueryOpen: boolean;
    onDuplicateQueryOpen: () => void;
    onDuplicateQueryClose: () => void;
    duplicateQuery: (params: { queryKeyInput: string }) => Promise<void>;
    isDuplicateLoading: boolean;
  };
  deleteParams: {
    isDeleteConfirmationModalOpen: boolean;
    onDeleteConfirmationModalOpen: () => void;
    onDeleteConfirmationModalClose: () => void;
    deleteQuery: () => Promise<void>;
    isDeleteLoading: boolean;
  };
  changeQueryString: (params: {
    newQueryString: string;
    queryInputDateForColumnUpdate: Util.FullDateStr;
  }) => Promise<void>;
  isQueryStringSaveLoading: boolean;
  presetVariables: CreditAgreement.Config.QueryVariable[];
  validationVariables: CreditAgreement.Config.QueryVariable[];
  inputDateType: 'reportingDate' | 'triggerDate';
  selectedInputDate?: Util.FullDateStr;
  queryDataItems: DataLake.AthenaQueryItemData[];
  isPreviewQueryResultsDrawerOpen: boolean;
  onClosePreviewQueryResultsDrawer: () => void;
};
export const LenderAthenaQueryView = ({
  athenaQuery,
  wipConfig,
  fullPropertyKey,
  duplicateParams,
  deleteParams,
  changeQueryString,
  isQueryStringSaveLoading,
  presetVariables,
  validationVariables,
  inputDateType,
  selectedInputDate,
  queryDataItems,
  isPreviewQueryResultsDrawerOpen,
  onClosePreviewQueryResultsDrawer,
}: LenderAthenaQueryViewProps) => {
  const [queryStringState, setQueryStringState] = useState<string>(
    athenaQuery.query.queryString,
  );

  useEffect(() => {
    setQueryStringState(athenaQuery.query.queryString);
  }, [fullPropertyKey, setQueryStringState, athenaQuery]);

  const {
    isDuplicateQueryOpen,
    onDuplicateQueryOpen,
    onDuplicateQueryClose,
    duplicateQuery,
    isDuplicateLoading,
  } = duplicateParams;
  const {
    isDeleteConfirmationModalOpen,
    onDeleteConfirmationModalOpen,
    onDeleteConfirmationModalClose,
    deleteQuery,
    isDeleteLoading,
  } = deleteParams;
  const queryStringEditModeDisclosure = useDisclosure();

  const inputDate = useMemo(
    () => selectedInputDate ?? commonDate.currentDateStr(),
    [selectedInputDate],
  );

  return (
    <Box>
      <Flex alignItems="center" mb="24px" justifyContent="right">
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <CopyIconButton
              ariaLabel="duplicate query"
              label="Duplicate Query"
              onClick={() => onDuplicateQueryOpen()}
            />
            <DeleteIconButton
              ariaLabel="delete query"
              label="Delete Query"
              onClick={() => onDeleteConfirmationModalOpen()}
            />
            <AddQueryInputModal
              wipConfig={wipConfig}
              isOpen={isDuplicateQueryOpen}
              onClose={onDuplicateQueryClose}
              onClickAdd={duplicateQuery}
              initialQueryType="singleQuery"
              isAddLoading={isDuplicateLoading}
              mode="duplicate"
            />
            <DeleteConfirmationModal
              isOpen={isDeleteConfirmationModalOpen}
              onClose={onDeleteConfirmationModalClose}
              onClickDelete={deleteQuery}
              isLoading={isDeleteLoading}
            />
          </>
        )}
      </Flex>
      <Divider />
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mt="64px"
        mb="24px"
      >
        <Text fontWeight={600} fontSize="20px">
          Key
        </Text>
        <QueryInputTypeBadge type="singleQuery" />
      </Flex>
      <Text
        color={colors.black[70]}
        p="32px"
        borderWidth="1px"
        borderRadius="8px"
        borderColor={colors.gray[200]}
      >
        {athenaQuery.query.name}
      </Text>

      <Box mt="64px">
        {/* *** SQL Query Management *** */}
        <AthenaQueryStringWithSchemaSidebarContainer
          wipConfig={wipConfig}
          fullPropertyKey={`${fullPropertyKey}.queryString`}
          changeQueryString={changeQueryString}
          isQueryStringSaveLoading={isQueryStringSaveLoading}
          queryStringState={queryStringState}
          setQueryStringState={setQueryStringState}
          selectedInputDate={selectedInputDate}
          initialQueryString={athenaQuery.query.queryString}
          editModeDisclosure={queryStringEditModeDisclosure}
        />

        {/* *** Bindings Management *** */}
        <BindingContainer
          wipConfig={wipConfig}
          bindings={athenaQuery.bindings}
          bindingsPropertyKey={`${fullPropertyKey}.bindings`}
          presetVariables={presetVariables}
          validationVariables={validationVariables}
          query={athenaQuery}
          queryType="singleQuery"
        />

        {/* *** Preview and Save Query *** */}
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <Flex
            alignItems="center"
            width="100%"
            justifyContent="flex-end"
            mt="64px"
          >
            <Button
              color="white"
              backgroundColor={colors.aurora[400]}
              isDisabled={isQueryStringSaveLoading}
              isLoading={isQueryStringSaveLoading}
              onClick={async () => {
                await changeQueryString({
                  newQueryString: queryStringState,
                  queryInputDateForColumnUpdate:
                    selectedInputDate ?? commonDate.currentDateStr(),
                });

                queryStringEditModeDisclosure.onClose();
              }}
            >
              Save & Preview Query
            </Button>
            <LenderAthenaQueryPreviewDrawer
              isOpen={isPreviewQueryResultsDrawerOpen}
              onClose={onClosePreviewQueryResultsDrawer}
              queryDataItems={queryDataItems}
              athenaQuery={athenaQuery}
              queryType="singleQuery"
              inputDate={inputDate}
              inputDateType={inputDateType}
            />
          </Flex>
        )}
      </Box>
    </Box>
  );
};
